import {
  SegmentDto,
  SegmentResponse,
  StepDto,
  StepResponse,
  WorkflowDefinitionDto,
  ConditionalGroupDto,
  ActionDto,
  SaveConditionalGroup,
  ComparisonOperatorEnum,
} from 'workflow-definition/openapi';
import { ConditionalValueDto, SaveConditionalValue } from '../models';
import { DROPDOWN_OPERATORS } from '../mocks/data';
import {
  Condition,
  ConditionalRefType,
  SegmentSelected,
  SetCondition,
  TreeSelectOptions,
} from '../models';
import { SET_CONDITIONS } from '../constants/workflow';
import { FormGroup } from '@angular/forms';
import { moveItemInArray } from '@angular/cdk/drag-drop';

/**
 * Updates the actions of a given step with new data.
 * @param oldStep - The original step data.
 * @param newData - The new step response data to be added.
 * @returns The updated step data.
 */
export const updateStepActions = (
  oldStep: StepDto,
  newData: StepResponse
): StepDto => {
  const existingActions = oldStep.actions || [];
  const actionIndex = existingActions.findIndex(
    action => action.id === newData.id
  );
  const updatedActions =
    actionIndex !== -1
      ? [
          ...existingActions.slice(0, actionIndex),
          { ...newData },
          ...existingActions.slice(actionIndex + 1),
        ]
      : [...existingActions, { ...newData }];
  return {
    ...oldStep,
    actions: [...updatedActions],
  };
};

/**
 * Updates the actions of steps within a segment.
 * @param oldSegment - The original segment data.
 * @param stepId - The ID of the step to be updated.
 * @param newData - The new step response data to be added.
 * @returns The updated segment data.
 */
export const updateSegmentStepsActions = (
  oldSegment: SegmentDto,
  newData: StepResponse,
  stepId: number
): SegmentDto => {
  const steps = oldSegment.steps?.map(step => {
    if (step.id === stepId) {
      return updateStepActions(step, newData);
    }
    return step;
  });
  return {
    ...oldSegment,
    steps,
  };
};

/**
 * Adds a new step to a segment.
 * @param oldSegment - The original segment data.
 * @param newStep - The new step response data to be added.
 * @returns The updated segment data.
 */
export const updateSegmentSteps = (
  oldSegment: SegmentDto,
  newStep: StepResponse
): SegmentDto => {
  let updatedStepId;
  let updatedSteps = (oldSegment?.steps || []).map(step => {
    if (step.id === newStep.id) {
      updatedStepId = newStep.id;
      return {
        ...newStep,
        conditionals: step.conditionals,
        blockers: step.blockers,
        actions: step.actions,
      };
    }
    return step;
  });

  if (!updatedStepId) {
    updatedSteps = [...updatedSteps, newStep];
  }

  return {
    ...oldSegment,
    steps: updatedSteps,
  };
};

/**
 * Updates the steps and actions within a workflow segment.
 * @param oldWorkflow - The original workflow data.
 * @param newData - The new step response data to be added.
 * @param segmentId - The ID of the segment to be updated.
 * @param stepId - (Optional) The ID of the step to be updated.
 * @returns The updated workflow data.
 */
export const updateWorkflowSegmentsStepsActions = (
  oldWorkflow: WorkflowDefinitionDto,
  newData: StepResponse,
  segmentId: number,
  stepId?: number
): WorkflowDefinitionDto => {
  const updateSegment = (segment: SegmentDto): SegmentDto => {
    if (stepId) {
      return updateSegmentStepsActions(segment, newData, stepId);
    } else {
      return updateSegmentSteps(segment, newData);
    }
  };

  const segments = oldWorkflow.segments?.map(segment => {
    if (segment.id === segmentId) {
      return updateSegment(segment);
    }
    return segment;
  });

  return {
    ...oldWorkflow,
    segments,
  };
};

/**
 * Updates the segments within a workflow.
 * @param oldWorkflow - The original workflow data.
 * @param newData - The new segment response data to be added.
 * @param segmentRefId - The ID of the segment to be updated.
 * @returns The updated workflow data.
 */
export const updateWorkflowSegments = (
  oldWorkflow: WorkflowDefinitionDto,
  newData: SegmentResponse,
  segmentRefId: number
): WorkflowDefinitionDto => {
  const segments =
    segmentRefId === 0
      ? [...(oldWorkflow.segments || []), newData]
      : [...(oldWorkflow.segments || [])].map(obj =>
          obj.id === newData?.id
            ? {
                ...obj,
                name: newData?.name,
                isEnforcedOrder: newData?.isEnforcedOrder,
                isRequired: newData?.isRequired,
                isVisible: newData?.isVisible,
                type: newData?.type,
              }
            : obj
        );

  return {
    ...oldWorkflow,
    segments,
  };
};

export function reOrderSegments(
  old: WorkflowDefinitionDto,
  segmentId?: number,
  segmentOrder?: number | null
): WorkflowDefinitionDto {
  let segmentListBeforeReOrdering = Object.assign<SegmentDto[], SegmentDto[]>(
    [],
    old.segments ?? []
  );
  if (segmentListBeforeReOrdering && typeof segmentOrder === 'number') {
    const indexOfSegment = segmentListBeforeReOrdering.findIndex(
      (el: SegmentDto) => el.id === segmentId
    );
    moveItemInArray(segmentListBeforeReOrdering, indexOfSegment, segmentOrder);
    segmentListBeforeReOrdering = segmentListBeforeReOrdering.map(
      (el, index) => {
        return {
          ...el,
          order: index,
        };
      }
    );
  }

  return {
    ...old,
    segments: segmentListBeforeReOrdering,
  };
}

export function getSegmentById(
  segments: SegmentSelected[],
  segmentId: number | undefined
) {
  return segments.find(el => el.segmentId === segmentId);
}

export function getTreeCondition(
  workflow: WorkflowDefinitionDto | undefined,
  currentReference?: string | null | undefined,
  typeReference?: string | null | undefined
): TreeSelectOptions[] {
  if (!workflow || !workflow?.segments) return [];
  // The current reference is the last selectable item; everything after it is excluded,
  // except that an action also keeps the actions that follow it in its own step.
  // Self-reference is intentional (confirmed by the PO), so the current item stays selectable.
  // `hasReachedCurrentReference` flips true when the current element is encountered, after which
  // no further items are pushed.
  let hasReachedCurrentReference = false;
  const result: TreeSelectOptions[] = [];

  workflow.segments.forEach((segment: SegmentDto, i) => {
    if (hasReachedCurrentReference) return;

    if (
      typeReference === 'segment' &&
      segment.id?.toString() === currentReference
    ) {
      hasReachedCurrentReference = true;
    }

    const steps: TreeSelectOptions[] = [];

    (segment.steps ?? []).forEach((step: StepDto) => {
      if (hasReachedCurrentReference) return;

      if (
        typeReference === 'step' &&
        step.id?.toString() === currentReference
      ) {
        hasReachedCurrentReference = true;
      }

      const actions: TreeSelectOptions[] = [];
      // For an action reference, the rest of its own step stays selectable (later actions
      // in the same step); only later steps and segments are cut off.
      let isCurrentActionStep = false;

      (step.actions ?? []).forEach((action: ActionDto) => {
        if (hasReachedCurrentReference && !isCurrentActionStep) return;

        if (
          typeReference === 'action' &&
          action.id?.toString() === currentReference
        ) {
          hasReachedCurrentReference = true;
          isCurrentActionStep = true;
        }

        actions.push({
          key: segment.id + '.' + step.id + '.' + action?.id,
          label: action.name || '',
          data: action.id?.toString() || '',
          actionId: action.actionId,
          tag: action.type || '',
          selectable: true,
          refType: 'action' as const,
        });
      });

      steps.push({
        key: segment.id + '.' + step.id,
        label: step.name || '',
        data: step.id?.toString() || '',
        selectable: true,
        refType: 'step' as const,
        children: actions as never,
      });
    });

    result.push({
      key: segment.name || i.toString(),
      label: segment.name || '',
      data: segment.id?.toString() || '',
      selectable: true,
      refType: 'segment' as const,
      children: steps as never,
    });
  });

  return result;
}

export function getEligibleSourceTree(
  workflow: WorkflowDefinitionDto | undefined,
  currentSegmentId: number | undefined,
  currentStepId: number | undefined
): TreeSelectOptions[] {
  if (!workflow?.segments) return [];

  const currentSegment = workflow.segments.find(s => s.id === currentSegmentId);
  // currentSegmentId is -1 when nothing is selected, so the lookup misses. Fail
  // closed: an Infinity fallback made every comparison below pass and exposed
  // the whole workflow as eligible.
  if (!currentSegment) return [];

  const currentStep = currentSegment.steps?.find(s => s.id === currentStepId);
  const currentSegOrder = currentSegment.order ?? 0;
  const currentStepOrder = currentStep?.order ?? 0;

  const result: TreeSelectOptions[] = [];

  for (const segment of workflow.segments) {
    const segOrder = segment.order ?? 0;
    if (segOrder > currentSegOrder) continue;

    const eligibleSteps: TreeSelectOptions[] = [];

    for (const step of segment.steps ?? []) {
      const stepOrder = step.order ?? 0;
      const isEligible =
        segOrder < currentSegOrder ||
        (segOrder === currentSegOrder && stepOrder < currentStepOrder);
      if (!isEligible) continue;

      const actions: TreeSelectOptions[] = (step.actions ?? []).map(action => ({
        key: `${segment.id}.${step.id}.${action.id}`,
        label: action.name || '',
        data: action.id?.toString() || '',
        actionId: action.actionId,
        tag: action.type || '',
        selectable: true,
        refType: 'action' as const,
      }));

      if (actions.length > 0) {
        eligibleSteps.push({
          key: `${segment.id}.${step.id}`,
          label: step.name || '',
          data: step.id?.toString() || '',
          selectable: false,
          refType: 'step' as const,
          children: actions as never,
        });
      }
    }

    if (eligibleSteps.length > 0) {
      result.push({
        key: segment.id?.toString() || '',
        label: segment.name || '',
        data: segment.id?.toString() || '',
        selectable: false,
        refType: 'segment' as const,
        children: eligibleSteps as never,
      });
    }
  }

  return result;
}

export function getSetConditions(
  workflow: WorkflowDefinitionDto | undefined,
  currentReference?: string | null | undefined,
  typeReference?: string | null | undefined
): SetCondition {
  if (!workflow || !workflow?.segments) return SET_CONDITIONS;

  const setConditions = getTreeCondition(
    workflow,
    currentReference,
    typeReference
  );

  return {
    ...SET_CONDITIONS,
    treeSelectOptionsIf: setConditions ?? [],
    treeSelectOptionsThen: setConditions ?? [],
  };
}

export function isCombinedCondition(conditions: Condition[]) {
  if (!conditions || conditions.length === 0) return false;
  const initialOperator = conditions[0].andor;
  return conditions.some(el => el.andor !== initialOperator);
}

function getComparisonValue(comparison: string | null | undefined) {
  if (!comparison) return '';
  return DROPDOWN_OPERATORS.find(el => el.value === comparison)?.label;
}

function getRefIdFromValue(
  val: ConditionalValueDto | undefined
): string | null {
  if (!val) return null;
  return (
    val.componentRefId?.toString() ??
    val.actionRefId?.toString() ??
    val.segmentRefId?.toString() ??
    val.stepRefId?.toString() ??
    null
  );
}

function getRefTypeFromValue(
  val: ConditionalValueDto | undefined
): ConditionalRefType | undefined {
  if (!val) return undefined;
  if (val.componentRefId) return 'component';
  if (val.actionRefId) return 'action';
  if (val.segmentRefId) return 'segment';
  if (val.stepRefId) return 'step';
  return undefined;
}

function findLabelByRefId(
  workflow: WorkflowDefinitionDto | null | undefined,
  refId: string | null | undefined
): string | null {
  if (!refId || !workflow?.segments) return null;
  for (const segment of workflow.segments) {
    if (segment.id?.toString() === refId) return segment.name ?? null;
    for (const step of segment.steps ?? []) {
      if (step.id?.toString() === refId) return step.name ?? null;
      for (const action of step.actions ?? []) {
        if (action.id?.toString() === refId) return action.name ?? null;
      }
    }
  }
  return null;
}

export function parseConditionals(
  conditionals: ConditionalGroupDto[] | null | undefined,
  workflow?: WorkflowDefinitionDto | null
) {
  const conditions: Condition[] = [];

  if (!conditionals) return [];

  const filteredConditionals = conditionals.filter(conditional => {
    return conditional.rules?.some(rule => !rule.val1?.jsonFieldId);
  });

  filteredConditionals.forEach((el: ConditionalGroupDto) => {
    if (el?.rules && el?.rules?.length > 0) {
      const val1 = el.rules[0].val1 as ConditionalValueDto | undefined;
      const refId1 = getRefIdFromValue(val1);
      const refType1 = getRefTypeFromValue(val1);
      const jsonRefId1 = val1?.jsonFieldId?.toString();
      const val2 = el.rules[0].val2 as ConditionalValueDto | undefined;
      const refId2 = getRefIdFromValue(val2);
      const refType2 = getRefTypeFromValue(val2);
      const jsonRefId2 = val2?.jsonFieldId?.toString();

      const lookupId1 = refId1 ?? val1?.fixedValue;
      const label1 =
        val1?.propertyName ??
        findLabelByRefId(workflow, lookupId1) ??
        val1?.fixedValue;

      const lookupId2 = refId2 ?? val2?.fixedValue;
      const label2 =
        val2?.propertyName ??
        findLabelByRefId(workflow, lookupId2) ??
        val2?.fixedValue;

      conditions.push({
        checked: false,
        andor: el.opr?.toLowerCase(),
        data: {
          if: {
            label: label1,
            data: refId1,
            type: val1?.fixedValue,
            json: jsonRefId1 ?? null,
            componentRefId:
              refType1 === 'component'
                ? val1?.componentRefId?.toString()
                : null,
            refType: refType1,
          },
          comparison: {
            label: el?.rules?.length
              ? getComparisonValue(el.rules[0].comp)
              : '',
            value: el?.rules?.length ? el.rules[0].comp : '',
          },
          then: {
            label: label2,
            data: refId2,
            type: val2?.fixedValue,
            json: jsonRefId2 ?? null,
            refType: refType2,
          },
        },
      });
    }
  });
  return conditions;
}

export function parseConditionalsJson(
  conditionals: ConditionalGroupDto[] | null | undefined
) {
  const filteredConditionals = conditionals?.filter(conditional => {
    return conditional.rules?.some(rule => rule.val1?.jsonFieldId);
  });

  return {
    triggers: (filteredConditionals ?? []).map((trigger, triggerIndex) => {
      return {
        conditionalGroup: {
          id: null,
          order: triggerIndex,
          opr: trigger.opr,
          rules: (trigger.rules ?? []).map((rule, ruleIndex) => {
            return {
              conditionalRule: {
                id: null,
                order: ruleIndex,
                val1: {
                  segmentRefId: rule.val1?.segmentRefId,
                  stepRefId: rule.val1?.stepRefId,
                  actionRefId: rule.val1?.actionRefId,
                  componentRefId: (rule.val1 as ConditionalValueDto)
                    ?.componentRefId,
                  jsonFieldId: rule.val1?.jsonFieldId,
                  fixedValue: rule.val1?.fixedValue,
                  label: rule.val1?.propertyName ?? rule.val1?.fixedValue,
                },
                comp: {
                  value: rule.comp,
                  label: DROPDOWN_OPERATORS.find(
                    item => item.value === rule.comp
                  )?.label,
                },
                val2: {
                  segmentRefId: rule.val2?.segmentRefId,
                  stepRefId: rule.val2?.stepRefId,
                  actionRefId: rule.val2?.actionRefId,
                  componentRefId: (rule.val2 as ConditionalValueDto)
                    ?.componentRefId,
                  jsonFieldId: rule.val2?.jsonFieldId,
                  fixedValue: rule.val2?.fixedValue,
                  label: rule.val2?.propertyName ?? rule.val2?.fixedValue,
                },
                opr: rule.opr,
              },
            };
          }),
        },
      };
    }),
  };
}

function buildConditionalValue(
  node:
    | {
        key?: string;
        data?: string;
        label?: string;
        type?: string;
        json?: string;
        refType?: ConditionalRefType;
        componentRefId?: string | null;
      }
    | null
    | undefined
): SaveConditionalValue {
  if (!node || typeof node !== 'object') {
    return {
      segmentRefId: null,
      stepRefId: null,
      actionRefId: null,
      componentRefId: null,
      jsonFieldId: null,
      fixedValue: '',
      propertyName: null,
    };
  }
  const isInput = node.key === 'input';
  const hasKey = !!node.key;
  const refType = node.refType;

  // Fixed value / freeform input
  if (isInput) {
    return {
      segmentRefId: null,
      stepRefId: null,
      actionRefId: null,
      componentRefId: null,
      jsonFieldId: null,
      fixedValue: node.label || '',
      propertyName: null,
    };
  }

  // Legacy condition without key (loaded from API, backwards compatibility)
  if (!hasKey) {
    // Loaded componentRefId condition (from parseConditionals)
    if (node.componentRefId) {
      return {
        segmentRefId: null,
        stepRefId: null,
        actionRefId: null,
        componentRefId: Number(node.componentRefId),
        jsonFieldId: null,
        fixedValue: node.type || '',
        propertyName: null,
      };
    }
    // Use refType to dispatch to the correct ref field when available
    if (refType) {
      return {
        segmentRefId: refType === 'segment' ? node.data : null,
        stepRefId: refType === 'step' ? node.data : null,
        actionRefId: refType === 'action' ? node.data : null,
        componentRefId: null,
        jsonFieldId: node.json ?? null,
        fixedValue: node.type || '',
        propertyName: null,
      } as SaveConditionalValue;
    }
    return {
      segmentRefId: null,
      stepRefId: null,
      actionRefId: node.data ?? null,
      componentRefId: null,
      jsonFieldId: node.json ?? null,
      fixedValue: node.type || '',
      propertyName: null,
    } as SaveConditionalValue;
  }

  // Context-aware fixed values (True/False, COMPLETED/PENDING/SKIPPED)
  if (!refType) {
    return {
      segmentRefId: null,
      stepRefId: null,
      actionRefId: null,
      componentRefId: null,
      jsonFieldId: null,
      fixedValue: node.data || node.label || '',
      propertyName: null,
    };
  }

  // New tree-select selection with refType
  return {
    segmentRefId: refType === 'segment' ? node.data : null,
    stepRefId: refType === 'step' ? node.data : null,
    actionRefId: refType === 'action' ? node.data : null,
    componentRefId: refType === 'component' ? node.data : null,
    jsonFieldId: null,
    fixedValue: '',
    propertyName: null,
  } as SaveConditionalValue;
}

const NO_VALUE_OPERATORS = ['IsCompleted', 'IsIncomplete'];

export function formatConditions(
  typeCondition: string | undefined,
  topOperator: string | undefined,
  conditions: FormGroup[]
) {
  if (!conditions) return [];
  const conditionals: SaveConditionalGroup[] = [];
  conditions.forEach((el, newOrder) => {
    const value = el.value ?? el;
    const comp = value.data.comparison.value as ComparisonOperatorEnum;
    const isNoValueOperator = NO_VALUE_OPERATORS.includes(comp);
    const val1 = buildConditionalValue(value.data.if);
    if (isNoValueOperator) {
      val1.propertyName = null;
    }
    conditionals.push({
      order: value.order ?? newOrder,
      opr:
        typeCondition === 'simple'
          ? topOperator?.toLocaleUpperCase()
          : value.andor.toLocaleUpperCase(),
      rules: [
        {
          val1,
          comp,
          val2: isNoValueOperator
            ? undefined
            : buildConditionalValue(value.data.then),
          opr: value.andor.toLocaleUpperCase(),
        },
      ],
    });
  });
  return conditionals;
}
